import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Box, Button, TextField, Typography } from '@mui/material';
import { WizardAccordion } from '../../components/WizardAccordion';
import { ImageThumbnail } from '../../components/ImageThumbnail';
import { DynamicTextFields } from '../../components/DynamicTextFields';
import { StarredSelect } from '../../components/StarredSelect';
import { PrivacyFieldContainer } from '../../components/PrivacyFieldContainer';
import { RevealableSection } from '../../components/RevealableSection';
import { AsWrittenSubsection } from '../../components/edit-object/AsWrittenSubsection';

import { ObjectMetadataValue } from '../../types/ObjectHierarchicalMetadataValue';
import { DatePickerField } from '../../components/DatePickerField';

import { SignatureFields } from '../../components/SignatureFields';
import { AutocompleteFieldMultiSelectWithFavorites } from '../../components/AutocompleteFieldMultiSelectWithFavorites';
import { useSignatureData } from '../../hooks/useSignatureData';
import { en } from '../../translations/en';
import { useCatalogDescriptions } from '../../hooks/useCatalogDescriptions';
import { useCatalog } from '../../hooks/useCatalog';
import type { DescriptionData as SaveDescriptionData } from '../../contexts/CatalogContext';
import { useUpload } from '../../hooks/useUpload';
import { useWizardCompletion } from '../../hooks/useWizardCompletion';
import { useAutosaveStatus } from '../../hooks/useAutosaveStatus';
import { useRegisterSaveNow } from '../../hooks/useRegisterSaveNow';
import { useUserProfile } from '../../hooks/useUserProfile';
import { useAuthorization } from '../../hooks/useAuthorization';
import { useActiveCatalog } from '../../hooks/useActiveCatalog';
import { getArtistDisplayName } from '../../utils/displayName';
import { filterDateLocations, filterTitleAndSignatureLocations } from '../../utils/locationFilters';
import { getCreatorDisplayName } from '../../utils/stringHelpers';
import { logger } from '../../utils/logger';
import { resolveOwnerCreatorName } from './describeTabOwnerName';

/** Merge old per-section notes into a single 'notes' key (one-way migration). */
const mergeOldNotes = (stepNotes: Record<string, string[]>): Record<string, string[]> => {
  if (stepNotes.notes) return stepNotes;
  const oldKeys = Object.keys(stepNotes).filter(k => k !== 'notes');
  const merged = oldKeys.flatMap(k => stepNotes[k] || []).filter(Boolean);
  return merged.length > 0 ? { notes: merged } : {};
};

/**
 * Determine if we should preserve the existing name from database.
 * Uses the same validation logic as getCreatorDisplayName to avoid duplication.
 * @param name - The creator name to validate
 * @returns true if the name is valid (not empty, not GUID, not "anon")
 */
const isValidCreatorName = (name: string | undefined): boolean => {
  return name !== undefined && getCreatorDisplayName(name) !== 'Unknown Artist';
};

/**
 * Helper function to process loaded description data
 * Used by both initial load and error recovery
 */
const processLoadedDescription = (
  typedData: DescriptionData | null,
  displayName: string,
  isCommercial: boolean,
  // True when the catalog has a distinct persona that should override any
  // stored isCurrentUser name (delegation or own add-on catalog). False for
  // the user's own primary catalog, where we only replace invalid names.
  shouldOverrideStoredName: boolean,
  callbacks: {
    setCreators: (creators: Array<{ name: string; isCurrentUser: boolean; notes?: string }>) => void;
    setTitle: (title: string) => void;
    setTitleLocation: (loc: string[]) => void;
    setAlternativeTitles: (titles: string[]) => void;
    setTitleAsWritten: (title: string) => void;
    setSignatures: (sigs: Array<{ type: string[]; location: string[]; display: string }>) => void;
    setCreationDate: (date: { type: string; value: string; location: string[]; display: string }) => void;
    setStepNotes: (notes: Record<string, string[]>) => void;
    setSelectedDateType: (type: string) => void;
    setIsInitialized: (init: boolean) => void;
    setIsLoadingInitialData: (loading: boolean) => void;
    batchUpdateTab: (tab: string, updates: Record<number, boolean>) => void;
    mediaFieldsRef: React.MutableRefObject<MediaFields>;
    lastSaveRef: React.MutableRefObject<string>;
  }
) => {
  if (typedData) {
    const loadedCreators = typedData.creators || [];
    const updatedCreators = isCommercial
      ? loadedCreators
      : loadedCreators.map(c =>
          c.isCurrentUser
            ? {
                ...c,
                // When the catalog has a distinct persona (delegation or own
                // add-on), always use the persona name — isCurrentUser was
                // set by whoever saved the record, possibly under a different
                // persona. For the primary catalog, only replace invalid
                // names (anon, GUID, empty) to preserve user edits.
                name: shouldOverrideStoredName
                  ? getArtistDisplayName(displayName)
                  : isValidCreatorName(c.name)
                    ? c.name
                    : getArtistDisplayName(displayName)
              }
            : c
        );

    let parsedSignatures: Array<{ type: string | string[]; location: string | string[]; display: string }> = [{ type: [], location: [], display: '' }];
    if (typedData.signatures) {
      try {
        parsedSignatures = typeof typedData.signatures === 'string' ? JSON.parse(typedData.signatures) : typedData.signatures;
      } catch (e) {
        logger.error('Failed to parse signatures', e);
      }
    }

    let parsedCreationDate: { type: string; value: string; location: string | string[]; display: string } = { type: '', value: '', location: [], display: '' };
    if (typedData.creationDate) {
      try {
        parsedCreationDate = typeof typedData.creationDate === 'string' ? JSON.parse(typedData.creationDate) : typedData.creationDate;
      } catch (e) {
        logger.error('Failed to parse creationDate', e);
      }
    }
    const migratedDateLocation = typeof parsedCreationDate.location === 'string'
      ? (parsedCreationDate.location ? [parsedCreationDate.location] : [])
      : (parsedCreationDate.location || []);
    parsedCreationDate = { ...parsedCreationDate, location: migratedDateLocation };

    const defaultCreators = isCommercial
      ? []
      : [{ name: getArtistDisplayName(displayName), isCurrentUser: true }];
    callbacks.setCreators(updatedCreators.length > 0 ? updatedCreators : defaultCreators);
    callbacks.setTitle(typedData.title || '');
    const rawTitleLoc = typedData.titleLocation;
    const migratedTitleLoc = typeof rawTitleLoc === 'string'
      ? (rawTitleLoc ? [rawTitleLoc] : [])
      : (rawTitleLoc || []);
    callbacks.setTitleLocation(migratedTitleLoc);
    callbacks.setAlternativeTitles(typedData.alternativeTitles || []);
    callbacks.setTitleAsWritten(typedData.titleAsWritten || '');
    const migratedSignatures = parsedSignatures.map(sig => ({
      ...sig,
      type: Array.isArray(sig.type) ? sig.type : (sig.type ? [sig.type] : []),
      location: Array.isArray(sig.location) ? sig.location : (sig.location ? [sig.location] : []),
    }));
    callbacks.setSignatures(migratedSignatures);
    const normalizedDateType = parsedCreationDate?.type === 'Date/Month/Year'
      ? 'Month/Day/Year'
      : (parsedCreationDate?.type || 'Year');
    const normalizedCreationDate = parsedCreationDate
      ? { ...parsedCreationDate, type: normalizedDateType || parsedCreationDate.type }
      : parsedCreationDate;
    callbacks.setCreationDate(normalizedCreationDate);
    callbacks.mediaFieldsRef.current = {
      mediaDescription: typedData.mediaDescription || '',
      workType: typedData.workType || null,
      hasMultiples: typedData.hasMultiples || false,
      editions: typedData.editions || [],
      techniques: typedData.techniques || [],
      materials: typedData.materials || [],
      substrate: typedData.substrate || [],
    };
    callbacks.setStepNotes(mergeOldNotes(typedData.stepNotes || {}));
    callbacks.setSelectedDateType(normalizedDateType);
    const descriptionData = {
      creators: updatedCreators.length > 0 ? updatedCreators : defaultCreators,
      title: typedData.title || '',
      titleLocation: migratedTitleLoc,
      alternativeTitles: typedData.alternativeTitles || [],
      titleAsWritten: typedData.titleAsWritten || '',
      signatures: migratedSignatures,
      creationDate: normalizedCreationDate,
      ...callbacks.mediaFieldsRef.current,
      stepNotes: typedData.stepNotes || {}
    };
    callbacks.lastSaveRef.current = JSON.stringify(descriptionData);

    const creatorsComplete = isCommercial
      ? updatedCreators.some(c => c.name.trim() !== '')
      : (updatedCreators.find(c => c.isCurrentUser)?.name || '').trim() !== '';
    callbacks.batchUpdateTab('describe', {
      0: creatorsComplete,
      1: (typedData.title || '').trim() !== '',
      2: migratedSignatures.length > 0 && migratedSignatures.some(s => s.type.length > 0 || s.location.length > 0 || s.display.trim()),
      3: parsedCreationDate.value !== '',
    });
  } else {
    const defaultCreators = isCommercial ? [] : [{ name: getArtistDisplayName(displayName), isCurrentUser: true }];
    callbacks.setCreators(defaultCreators);
    callbacks.setSignatures([{ type: [], location: [], display: '' }]);
  }
  callbacks.setIsInitialized(true);
  callbacks.setIsLoadingInitialData(false);
};

interface DescriptionData {
  creators?: Array<{ name: string; isCurrentUser: boolean; notes?: string }>;
  title?: string;
  titleLocation?: string | string[];
  alternativeTitles?: string[];
  titleAsWritten?: string;
  signatures?: Array<{ type: string | string[]; location: string | string[]; display: string }> | string;
  creationDate?: { type: string; value: string; location: string | string[]; display: string } | string;
  workType?: ObjectMetadataValue | null;
  hasMultiples?: boolean;
  editions?: Array<{ type: string; count: number; total: number }>;
  techniques?: ObjectMetadataValue[];
  materials?: ObjectMetadataValue[];
  substrate?: ObjectMetadataValue[];
  mediaDescription?: string;
  stepNotes?: Record<string, string[]>;
}

/** Fields owned by MediaTab that we pass through on save */
interface MediaFields {
  mediaDescription: string;
  workType: ObjectMetadataValue | null;
  hasMultiples: boolean;
  editions: Array<{ type: string; count: number; total: number }>;
  techniques: ObjectMetadataValue[];
  materials: ObjectMetadataValue[];
  substrate: ObjectMetadataValue[];
}

interface DescribeTabProps {
  catalogItemId?: string;
  ownerId?: string | null;
  /**
   * Explicit create-vs-edit signal forwarded from UploadObject.
   * When false (create wizard), the auto-title feature (GH#1225) is active.
   * When true (edit mode), the suggested title is NEVER applied regardless of
   * whether catalogItemId is set — the catalogItemId alone cannot distinguish
   * the two modes because UploadObject always passes a truthy effectiveCatalogId
   * even during the create flow (it is the freshly-minted draft catalog id).
   */
  isEditMode?: boolean;
}

export const DescribeTab = ({ catalogItemId, ownerId, isEditMode = false }: DescribeTabProps) => {
  const [hasError, setHasError] = useState(false);

  const { currentItem } = useCatalog();
  const { catalogId, setCatalogTitle, suggestedTitle } = useUpload();
  const effectiveCatalogId = catalogItemId || catalogId || currentItem?.catalogItemId;
  const { loadDescription, updateDescription } = useCatalogDescriptions(effectiveCatalogId, ownerId);
  const { signatureLocations, loading: signatureLoading } = useSignatureData();
  const dateLocations = useMemo(() => filterDateLocations(signatureLocations), [signatureLocations]);
  const titleLocations = useMemo(() => filterTitleAndSignatureLocations(signatureLocations), [signatureLocations]);
  const { batchUpdateTab } = useWizardCompletion();
  const { setStatus, setLastSaveTime } = useAutosaveStatus();
  const { displayName } = useUserProfile();
  const { activeDelegation, activeCatalogAliId, ownedCatalogs, isViewingDelegatedCatalog, delegationsFetched } = useActiveCatalog();
  // Resolve the active owned-catalog row when the user has selected one
  // of their own add-ons — only `activeCatalogAliId` flips for that
  // selection, leaving `activeDelegation` null.
  const activeOwnedCatalog = useMemo(
    () =>
      !activeDelegation && activeCatalogAliId
        ? ownedCatalogs?.find(c => c.catalogAliId === activeCatalogAliId) ?? null
        : null,
    [activeDelegation, activeCatalogAliId, ownedCatalogs]
  );
  // Three-way resolution: delegation → own add-on → user display name.
  // `shouldOverrideStoredName` mirrors the old `isDelegatedCatalog` flag
  // for callers below — when the catalog has a distinct persona (delegation
  // or own add-on), we override stored `isCurrentUser` creator names with
  // the persona name; otherwise we preserve the legacy "only replace
  // invalid names" behaviour.
  // `ownerNameReady` is false on a delegated catalog until the delegator's
  // name has loaded — we defer the initial owner-creator processing until then
  // so an early autosave can never stamp the delegate's own name onto the
  // delegator's item (the load-order race that mis-saved EB-5XX items).
  const { name: ownerDisplayName, shouldOverrideStoredName, ready: ownerNameReady } = useMemo(
    () =>
      resolveOwnerCreatorName({
        activeDelegation,
        activeOwnedCatalog,
        userDisplayName: displayName,
        isViewingDelegatedCatalog,
        delegationContextLoaded: delegationsFetched,
      }),
    [activeDelegation, activeOwnedCatalog, displayName, isViewingDelegatedCatalog, delegationsFetched]
  );
  const { isCommercialTier } = useAuthorization();
  const isCommercial = isCommercialTier();

  const [selectedDateType, setSelectedDateType] = useState('Year');

  // Passthrough ref for MediaTab's fields — populated on load, merged on save.
  // Trade-off: Each tab loads the full description record and stores the other tab's fields
  // in a ref. Refs are refreshed on each tab switch via loadDescription(). A narrow race exists
  // if MediaTab's debounced save hasn't completed when this tab's loadDescription fires,
  // but handleBlur (immediate save) on tab switch significantly narrows this window.
  // Long-term fix: partial-update API endpoint so each tab only saves its own fields.
  const mediaFieldsRef = useRef<MediaFields>({
    mediaDescription: '',
    workType: null,
    hasMultiples: false,
    editions: [],
    techniques: [],
    materials: [],
    substrate: [],
  });

  // State management
  const [isInitialized, setIsInitialized] = useState(false);
  const [isLoadingInitialData, setIsLoadingInitialData] = useState(false);
  const loadedCatalogIdRef = useRef<string | null>(null);

  // Form state
  const [creators, setCreators] = useState<Array<{ name: string; isCurrentUser: boolean; notes?: string }>>([]);
  const [title, setTitle] = useState('');
  const [titleLocation, setTitleLocation] = useState<string[]>([]);
  const [alternativeTitles, setAlternativeTitles] = useState<string[]>([]);
  const [titleAsWritten, setTitleAsWritten] = useState('');
  const [signatures, setSignatures] = useState<Array<{ type: string[]; location: string[]; display: string }>>([]);
  const [creationDate, setCreationDate] = useState<{ type: string; value: string; location: string[]; display: string }>({ type: 'Year', value: '', location: [], display: '' });
  const [stepNotes, setStepNotes] = useState<Record<string, string[]>>({});

  // GH#1225 (MEDIUM): Ref that always reflects the latest rendered title value.
  // Updated synchronously on every render so that async .then() callbacks can
  // read the current in-memory title without a stale-closure risk (the effects
  // suppress the exhaustive-deps lint rule, so `title` in those closures could
  // be from an earlier render cycle).
  const titleRef = useRef(title);
  titleRef.current = title;

  // Derived state: Separate owner creator from co-creators
  // Commercial tier has no owner creator concept — all artists are editable
  const ownerCreator = useMemo(() =>
    isCommercial
      ? null
      : creators.find(c => c.isCurrentUser) || {
          name: getArtistDisplayName(ownerDisplayName),
          isCurrentUser: true
        },
    [creators, ownerDisplayName, isCommercial]
  );

  // For commercial tier, all creators are treated as "artists" (no filter needed)
  const coCreators = useMemo(() =>
    isCommercial ? creators : creators.filter(c => !c.isCurrentUser),
    [creators, isCommercial]
  );

  // ---- Summary chips (collapsed-state previews shown in accordion headers) ----
  const creatorsSummary = useMemo(() => {
    const names = (isCommercial ? creators : [ownerCreator, ...coCreators])
      .map(c => c?.name?.trim())
      .filter((n): n is string => !!n);
    return names.length > 0 ? names.join(', ') : 'No creators added';
  }, [isCommercial, creators, ownerCreator, coCreators]);

  const titleSummary = useMemo(() => title.trim() || 'Untitled', [title]);

  const signatureSummary = useMemo(() => {
    const first = signatures[0];
    if (!first) return 'Not signed';
    const parts = [
      ...(first.type ?? []),
      ...(first.location ?? []),
    ].filter(Boolean);
    if (parts.length === 0 && !first.display?.trim()) return 'Not signed';
    return parts.join(' · ') || first.display?.trim() || 'Not signed';
  }, [signatures]);

  const creationDateSummary = useMemo(() => {
    return creationDate.value?.trim() || creationDate.display?.trim() || 'No date set';
  }, [creationDate]);

  // Notes: legacy data may be a multi-entry array; we join with newlines for the
  // single textarea (decision #3 in plan). Saves still write through stepNotes.notes
  // as an array (with at most one entry) to preserve the existing API shape.
  const notesText = useMemo(() => (stepNotes.notes ?? []).join('\n\n'), [stepNotes.notes]);
  const notesSummary = notesText.trim() ? notesText.slice(0, 80) + (notesText.length > 80 ? '…' : '') : 'No notes added';

  // Smart auto-save with multiple triggers
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const saveDataRef = useRef<(opts?: { bypassCheck?: boolean; forceWriteDuringLoad?: boolean }) => Promise<void> | Promise<boolean> | void>(() => {});
  const lastSaveRef = useRef<string>('');
  // Holds the resolver for a forced-save that was deferred while
  // isLoadingInitialData was true. The resolver is called (and the ref
  // cleared) by the useEffect that watches isLoadingInitialData.
  const pendingForcedSaveRef = useRef<(() => void) | null>(null);
  // Shared promise for any concurrent forced saves that arrive while loading.
  // A second forced save (e.g. double-click Finished) returns this same
  // promise instead of bare-resolving the first one — coalesces both callers
  // onto the same deferred write so neither navigates before the title is
  // persisted.
  const pendingForcedSavePromiseRef = useRef<Promise<void> | null>(null);
  // Safety-timeout ID for the deferred forced save.
  const forcedSaveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  // How long to wait for the load to settle before giving up on the deferred
  // forced save. On timeout: if the user typed a title, we best-effort persist
  // it; if the title is empty we resolve without writing (GL#40-safe — never
  // write empty defaults over a possibly-loading existing record).
  // 8 s is more generous than 5 s for genuinely slow connections while still
  // preventing navigation from blocking forever.
  const FORCED_SAVE_DEFER_TIMEOUT_MS = 8000;
  // Let the wizard "Finished" button flush this tab's pending save.
  useRegisterSaveNow(saveDataRef, saveTimeoutRef);

  // Completion tracking function (defined before saveData to avoid hoisting issues)
  // Uses batchUpdateTab for atomic updates to prevent race conditions during edit mode
  const updateCompletionTracking = useCallback(() => {
    // Commercial: at least one artist with a non-empty name
    // Creator tier: owner creator has a non-empty name
    const creatorsComplete = isCommercial
      ? creators.some(c => c.name.trim() !== '')
      : (ownerCreator?.name.trim() ?? '') !== '';

    batchUpdateTab('describe', {
      0: creatorsComplete,                // Creators / Artists
      1: title.trim() !== '',              // Title & Location
      2: signatures.length > 0 && signatures.some(s => s.type.length > 0 || s.location.length > 0 || s.display.trim()),  // Signature (any field)
      3: creationDate.value !== '',        // Creation Date
    });
  }, [batchUpdateTab, ownerCreator, creators, isCommercial, title, signatures, creationDate]);

  const saveData = useCallback(async (opts?: { bypassCheck?: boolean; forceWriteDuringLoad?: boolean }) => {
    const bypassCheck = opts?.bypassCheck === true;
    const forceWriteDuringLoad = opts?.forceWriteDuringLoad === true;

    // Hard guards: never save when there is no target or when the initial load
    // failed. For hasError we always skip even on a forced save — the load
    // failed so saving now would overwrite real data with empty defaults
    // (GL#40 root-cause preservation).
    if (!effectiveCatalogId || hasError) {
      return;
    }

    // isLoadingInitialData guard — split by caller intent:
    //  • background autosave (bypassCheck=false): skip, unchanged (GL#40).
    //  • user-initiated Finish (bypassCheck=true): DEFER — return a promise
    //    that resolves only after the load settles, then re-invoke saveData
    //    via the ref so the latest state (including any typed title) is used.
    //  • timeout best-effort write (bypassCheck=true AND forceWriteDuringLoad=true):
    //    FALL THROUGH to the real write path — the load never settled and we
    //    must not re-enter the defer branch (which would create a circular
    //    self-dependency: deferred.then(resolve) where resolve is deferred's
    //    own resolver → deadlock).
    if (isLoadingInitialData && !(bypassCheck && forceWriteDuringLoad)) {
      if (!bypassCheck) {
        return;
      }
      // Coalescing deferred forced save: if a deferred promise is already in
      // flight (e.g. double-click Finished), return the SAME promise so both
      // callers wait for the same write. Creating a second concurrent deferred
      // promise would bare-resolve the first one and allow the 1st handleFinished
      // to navigate before the title has been written — the re-entrancy bug.
      if (pendingForcedSavePromiseRef.current) {
        return pendingForcedSavePromiseRef.current;
      }
      // No deferred promise exists yet — create one. Register the resolver on
      // pendingForcedSaveRef so the useEffect([isLoadingInitialData]) can fire
      // it when loading clears. A safety timeout prevents navigation from
      // blocking forever; on timeout we best-effort persist a non-empty title.
      const deferred = new Promise<void>((resolve) => {
        pendingForcedSaveRef.current = resolve;
        forcedSaveTimeoutRef.current = setTimeout(() => {
          if (pendingForcedSaveRef.current === resolve) {
            pendingForcedSaveRef.current = null;
            forcedSaveTimeoutRef.current = null;
            // FIX B: on timeout, if the user typed a title best-effort persist
            // it rather than silently dropping it. The title ref is captured
            // via the closure; re-invoke via saveDataRef with forceWriteDuringLoad
            // so the real write path is taken even though isLoadingInitialData is
            // still true. If no title was typed, resolve without writing
            // (GL#40-safe — never write empty defaults over possibly-loading data).
            const titleSnapshot = title.trim();
            if (titleSnapshot !== '') {
              logger.warn('DescribeTab: forced save deferred past safety timeout — persisting typed title');
              const writePromise = saveDataRef.current?.({ bypassCheck: true, forceWriteDuringLoad: true });
              if (writePromise && typeof (writePromise as Promise<void>).then === 'function') {
                (writePromise as Promise<void>).then(resolve).catch((err) => {
                  logger.error('DescribeTab: timeout best-effort write failed', err);
                  resolve();
                });
              } else {
                resolve();
              }
            } else {
              logger.warn('DescribeTab: forced save deferred past safety timeout — resolving without write (empty title)');
              resolve();
            }
          }
        }, FORCED_SAVE_DEFER_TIMEOUT_MS);
      });
      // Store the shared promise so concurrent callers coalesce onto it, and
      // clear it when it settles so future forced saves create a fresh one.
      pendingForcedSavePromiseRef.current = deferred;
      deferred.finally(() => {
        pendingForcedSavePromiseRef.current = null;
      });
      return deferred;
    }

    // Merge own fields with passthrough ref (MediaTab fields) to rebuild full record
    const descriptionData: SaveDescriptionData = {
      creators: creators.filter(c => c?.name?.trim()),
      title,
      titleLocation,
      alternativeTitles: alternativeTitles.filter(t => t?.trim()),
      titleAsWritten,
      signatures,
      creationDate,
      ...mediaFieldsRef.current,
      stepNotes
    };

    const currentDataString = JSON.stringify(descriptionData);
    // Skip dedup when bypassCheck — the user explicitly requested a flush,
    // so always write even if the data string matches the last save.
    if (!bypassCheck && currentDataString === lastSaveRef.current) {
      return;
    }

    setStatus('saving');
    try {
      await updateDescription(descriptionData);
      lastSaveRef.current = currentDataString;

      // Update completion tracking
      updateCompletionTracking();

      setStatus('saved');
      setLastSaveTime(new Date());
    } catch (error) {
      setStatus('error');
      logger.error('Failed to auto-save', error);
    }
  }, [effectiveCatalogId, isLoadingInitialData, hasError, updateDescription, creators, title, titleLocation, alternativeTitles, titleAsWritten, signatures, creationDate, stepNotes, updateCompletionTracking, setStatus, setLastSaveTime]);

  // Use ref-based save to avoid stale closure issues
  // When state changes, React creates new function instances. If a debounced timeout
  // captured an old saveData, it would use stale state (e.g., title='' instead of loaded value).
  // Using saveDataRef.current ensures we always call the LATEST saveData function.
  const debouncedSave = useCallback(() => {
    // Signal immediately that there are unsaved edits — clears "All changes saved" (GL#40 Part 1).
    setStatus('dirty');
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    saveTimeoutRef.current = setTimeout(() => {
      saveDataRef.current?.();
    }, 2000);
  }, [setStatus]); // setStatus is stable from context

  // Save on blur (when user clicks away from field)
  // Uses ref to avoid stale closure - ensures we call the latest saveData
  const handleBlur = useCallback(() => {
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    saveDataRef.current?.(); // Immediate save on blur using ref
  }, []); // No dependencies - ref always has latest value

  // GH#1225 (LOW dedup + MEDIUM guard): single helper shared by both load effects.
  // Guards:
  //   1. !isEditMode          — never auto-title in edit mode
  //   2. !typedData?.title    — DB row has no title yet
  //   3. !titleRef.current    — in-memory title is also empty (MEDIUM: protects a
  //                             user-typed-but-not-yet-saved title on a load re-run)
  //   4. suggestedTitle       — a filename-derived suggestion is available
  const maybeApplySuggestedTitle = (typedData: DescriptionData | null) => {
    if (!isEditMode && !typedData?.title?.trim() && !titleRef.current?.trim() && suggestedTitle?.trim()) {
      setTitle(suggestedTitle);
    }
  };

  // Handle error recovery: when user clicks Retry, reload the data
  useEffect(() => {
    if (!hasError && loadedCatalogIdRef.current === null && effectiveCatalogId) {
      // Error was cleared and we need to reload
      loadedCatalogIdRef.current = effectiveCatalogId;
      setIsLoadingInitialData(true);
      loadDescription().then((data) => {
        const typedData = data as DescriptionData | null;
        processLoadedDescription(typedData, ownerDisplayName, isCommercial, shouldOverrideStoredName, {
          setCreators,
          setTitle,
          setTitleLocation,
          setAlternativeTitles,
          setTitleAsWritten,
          setSignatures,
          setCreationDate,
          setStepNotes,
          setSelectedDateType,
          setIsInitialized,
          setIsLoadingInitialData,
          batchUpdateTab,
          mediaFieldsRef,
          lastSaveRef,
        });
        maybeApplySuggestedTitle(typedData);
      }).catch((error) => {
        logger.error('DescribeTab: Failed to load description data on retry', error);
        setHasError(true);
        setIsInitialized(true);
        setIsLoadingInitialData(false);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasError, effectiveCatalogId]);

  // Load existing description data on mount or when catalogId changes
  useEffect(() => {
    // Defer the load on a delegated catalog until the delegator's name has
    // resolved (`ownerNameReady`). Processing now would default the
    // owner-creator to an unresolved name and an early autosave could persist
    // it. We hold `isLoadingInitialData` true so the autosave guard blocks any
    // write during the wait; the effect re-runs when `ownerNameReady` flips.
    // Own/primary catalogs are `ready` from first render, so they never wait.
    if (effectiveCatalogId && !ownerNameReady && loadedCatalogIdRef.current !== effectiveCatalogId) {
      setIsLoadingInitialData(true);
      return;
    }
    // Only load if we haven't already loaded this specific catalogId
    if (effectiveCatalogId && ownerNameReady && loadedCatalogIdRef.current !== effectiveCatalogId) {
      loadedCatalogIdRef.current = effectiveCatalogId;
      setIsLoadingInitialData(true);
      loadDescription().then((data) => {
        const typedData = data as DescriptionData | null;
        processLoadedDescription(typedData, ownerDisplayName, isCommercial, shouldOverrideStoredName, {
          setCreators,
          setTitle,
          setTitleLocation,
          setAlternativeTitles,
          setTitleAsWritten,
          setSignatures,
          setCreationDate,
          setStepNotes,
          setSelectedDateType,
          setIsInitialized,
          setIsLoadingInitialData,
          batchUpdateTab,
          mediaFieldsRef,
          lastSaveRef,
        });
        maybeApplySuggestedTitle(typedData);
      }).catch((error) => {
        logger.error('DescribeTab: Failed to load description data', error);
        setHasError(true);
        setIsInitialized(true);
        setIsLoadingInitialData(false);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveCatalogId, ownerNameReady]);

  // Update owner creator name when ownerDisplayName becomes available and current name is
  // empty or "anon". This handles two cases:
  // 1. Legacy users with displayName="anon" in DynamoDB (pre-#966)
  // 2. Race condition: profile loads after processLoadedDescription sets creator to ""
  // ownerDisplayName is the delegator's name when viewing a delegated catalog, otherwise the
  // logged-in user's own display name.
  // Skip for commercial tier — no owner creator concept.
  useEffect(() => {
    if (isCommercial) return;
    if (ownerDisplayName && isInitialized && !isLoadingInitialData && effectiveCatalogId) {
      setCreators(prev => {
        const hasInvalidOwner = prev.some(c =>
          c.isCurrentUser && (!c.name || c.name.trim() === '' || c.name.toLowerCase() === 'anon')
        );
        if (hasInvalidOwner) {
          return prev.map(c =>
            c.isCurrentUser
              ? { ...c, name: getArtistDisplayName(ownerDisplayName) }
              : c
          );
        }
        return prev;
      });
    }
  }, [ownerDisplayName, isInitialized, isLoadingInitialData, effectiveCatalogId, isCommercial]);

  // Immediate completion tracking when displayName or form fields change
  // This ensures the Creator checkmark shows correctly when user profile loads
  useEffect(() => {
    if (isInitialized && !isLoadingInitialData) {
      updateCompletionTracking();
    }
  }, [isInitialized, isLoadingInitialData, ownerCreator, title, signatures, creationDate, updateCompletionTracking]);

  // Keep the wizard reminder card in sync with the title field as the user types (#1021).
  // loadExistingCatalog only sets catalogTitle once on mount; this effect pushes live edits.
  useEffect(() => {
    setCatalogTitle(title);
  }, [title, setCatalogTitle]);

  // Cleanup timeout on unmount.
  // FIX D: also clear any stale forced-save timer and resolve any pending
  // deferred promise so it doesn't linger after the component is gone.
  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
      // Clear the forced-save safety timeout on unmount so no stale timer
      // fires after the component is gone and references stale closures.
      if (forcedSaveTimeoutRef.current) {
        clearTimeout(forcedSaveTimeoutRef.current);
        forcedSaveTimeoutRef.current = null;
      }
      // Resolve any pending deferred forced save so the caller (handleFinished)
      // is not left hanging indefinitely after an unmount.
      if (pendingForcedSaveRef.current) {
        pendingForcedSaveRef.current();
        pendingForcedSaveRef.current = null;
      }
      try {
        const maybePromise = saveDataRef.current?.({ bypassCheck: true });
        if (maybePromise && typeof (maybePromise as { then?: unknown }).then === 'function') {
          // don't await, but at least call and catch any rejection
          (maybePromise as Promise<void>).catch((err) =>
            logger.error('Flush save failed on unmount', err)
          );
        }
      } catch (err) {
        logger.error('Flush save failed on unmount (sync)', err);
      }
    };
  }, []);

  // FIX D: The saveDataRef update effect MUST be declared BEFORE the
  // deferred-resolver useEffect([isLoadingInitialData]) below. Declaration
  // order guarantees this effect runs first within the same render cycle, so
  // the resolver always re-invokes the FRESH saveData closure (including the
  // latest captured `title` state). Reordering these two effects would cause
  // the resolver to call a stale saveData that lacks the user's typed title.
  useEffect(() => {
    saveDataRef.current = saveData;
  }, [saveData]);

  // Deferred forced-save resolver: when isLoadingInitialData clears and a
  // forced save is pending (user clicked Finished during load), re-invoke
  // saveData via the ref so the latest state is used. Cancel the safety
  // timeout and resolve the outer promise returned to handleFinished.
  useEffect(() => {
    if (!isLoadingInitialData && pendingForcedSaveRef.current) {
      const resolve = pendingForcedSaveRef.current;
      pendingForcedSaveRef.current = null;
      if (forcedSaveTimeoutRef.current) {
        clearTimeout(forcedSaveTimeoutRef.current);
        forcedSaveTimeoutRef.current = null;
      }
      // Invoke via ref so we always use the freshest saveData closure.
      // The promise chain ensures resolve() is called only after the write.
      const maybePromise = saveDataRef.current?.({ bypassCheck: true });
      if (maybePromise && typeof (maybePromise as Promise<void>).then === 'function') {
        (maybePromise as Promise<void>).then(resolve).catch((err) => {
          logger.error('DescribeTab: deferred forced save failed', err);
          resolve();
        });
      } else {
        resolve();
      }
    }
  }, [isLoadingInitialData]);

  if (hasError) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="h6" color="error">
          Something went wrong loading the description form.
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Please try again or contact support if the issue persists.
        </Typography>
        <Button
          variant="outlined"
          sx={{ mt: 2 }}
          onClick={() => {
            setHasError(false);
            loadedCatalogIdRef.current = null;
          }}
        >
          Retry
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <ImageThumbnail />

      {/* Step 1: Creators / Artists */}
      <WizardAccordion
        id="describe-creators"
        title={isCommercial ? en.wizard.describe.steps.creators.commercial.title : en.wizard.describe.steps.creators.title}
        subtitle={isCommercial ? en.wizard.describe.steps.creators.commercial.subtitle : en.wizard.describe.steps.creators.subtitle}
        defaultExpanded={false}
        summary={creatorsSummary}
        privacyFieldPath="describe.creators"
      >
        {/* Owner Creator - Read Only (creator tier only) */}
        {!isCommercial && ownerCreator && (
          <Box sx={{ mb: 2 }}>
            <TextField
              fullWidth
              label={en.wizard.describe.steps.creators.ownerLabel}
              value={ownerCreator.name}
              variant="outlined"
              disabled
              InputProps={{
                readOnly: true,
              }}
              helperText={en.wizard.describe.steps.creators.ownerHelperText}
              aria-label="Creator"
              aria-readonly="true"
            />
          </Box>
        )}

        {/* Co-Creators (creator tier) / All Artists (commercial tier) - Editable */}
        <DynamicTextFields
          label={isCommercial ? en.wizard.describe.steps.creators.commercial.artistLabel : en.wizard.describe.steps.creators.coCreatorLabel}
          addButtonText={isCommercial ? en.wizard.describe.steps.creators.commercial.addButton : en.wizard.describe.steps.creators.addButton}
          initialValues={coCreators.map(c => c.name)}
          onChange={(values) => {
            if (isCommercial) {
              // Commercial: all entries are external artists
              const newCreators = values.map(name => ({
                name,
                isCurrentUser: false
              }));
              setCreators(newCreators);
            } else {
              // Creator tier: prepend owner creator
              const newCoCreators = values.map(name => ({
                name,
                isCurrentUser: false
              }));
              const newCreators = ownerCreator ? [ownerCreator, ...newCoCreators] : newCoCreators;
              setCreators(newCreators);
            }
            debouncedSave();
          }}
        />

      </WizardAccordion>

      {/* Step 2: Object Title */}
      <WizardAccordion
        id="describe-title"
        title={en.wizard.describe.steps.titleLocation.objectTitle.title}
        subtitle={en.wizard.describe.steps.titleLocation.objectTitle.subtitle}
        defaultExpanded={true}
        summary={titleSummary}
        privacyFieldPath="describe.title"
      >
        <TextField
          fullWidth
          placeholder={en.wizard.describe.steps.titleLocation.objectTitle.label}
          variant="outlined"
          value={title}
          onChange={(e) => {
            setTitle(e.target.value);
            debouncedSave();
          }}
          onBlur={handleBlur}
        />

        {/* "As written on the object" — groups Title Location + Title-as-Written
            into one reveal box, matching the design's subdued-subsection pattern. */}
        <AsWrittenSubsection
          defaultRevealed={titleLocation.length > 0 || titleAsWritten.trim().length > 0}
        >
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2">
              {en.wizard.describe.steps.titleLocation.locationOfTitle.title}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
              {en.wizard.describe.steps.titleLocation.locationOfTitle.subtitle}
            </Typography>
            <PrivacyFieldContainer fieldPath="describe.titleLocation" togglePosition="inline">
              <AutocompleteFieldMultiSelectWithFavorites
                label={en.wizard.describe.steps.titleLocation.locationOfTitle.label}
                value={titleLocation}
                options={titleLocations}
                onChange={(value) => {
                  setTitleLocation(value);
                  debouncedSave();
                }}
                loading={signatureLoading}
                vocabularyType="title_location"
              />
            </PrivacyFieldContainer>
          </Box>

          <Box>
            <Typography variant="subtitle2">
              {en.wizard.describe.steps.titleLocation.titleDisplay.title}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
              {en.wizard.describe.steps.titleLocation.titleDisplay.subtitle}
            </Typography>
            <PrivacyFieldContainer fieldPath="describe.titleAsWritten" togglePosition="inline">
              <TextField
                fullWidth
                placeholder={en.wizard.describe.steps.titleLocation.titleDisplay.label}
                variant="outlined"
                value={titleAsWritten}
                onChange={(e) => {
                  setTitleAsWritten(e.target.value);
                  debouncedSave();
                }}
                onBlur={handleBlur}
              />
            </PrivacyFieldContainer>
          </Box>
        </AsWrittenSubsection>

        {/* Alternative Titles - reveal on click (decision #5: keep this functionality) */}
        <RevealableSection
          label="+ Add Alternative Titles"
          revealedLabel="Hide Alternative Titles"
          defaultRevealed={alternativeTitles.length > 0}
        >
          <PrivacyFieldContainer fieldPath="describe.alternativeTitles" togglePosition="inline">
            <DynamicTextFields
              key={`alt-titles-${isInitialized}`}
              label={en.wizard.describe.steps.titleLocation.alternativeTitles.label}
              addButtonText={en.wizard.describe.steps.titleLocation.alternativeTitles.addButton}
              initialValues={alternativeTitles}
              onChange={(values) => {
                setAlternativeTitles(values);
                debouncedSave();
              }}
            />
          </PrivacyFieldContainer>
        </RevealableSection>

      </WizardAccordion>

      {/* Step 3: Signature */}
      <WizardAccordion
        id="describe-signature"
        title={en.wizard.describe.steps.signature.signatureType.title}
        subtitle={en.wizard.describe.steps.signature.locationOfSignature.subtitle}
        defaultExpanded={true}
        summary={signatureSummary}
        privacyFieldPath="describe.signature"
      >
        <SignatureFields
          value={signatures}
          onChange={(newSignatures) => {
            setSignatures(newSignatures);
            debouncedSave();
          }}
        />

      </WizardAccordion>

      {/* Step 4: Creation Date */}
      <WizardAccordion
        id="describe-creation-date"
        title={en.wizard.describe.steps.creationDate.creationDate.title}
        subtitle={en.wizard.describe.steps.creationDate.creationDate.subtitle}
        defaultExpanded={true}
        summary={creationDateSummary}
        privacyFieldPath="describe.creationDate"
      >
        {/* Top row: design's 3-col layout (Date type / Date / Location on object).
            Stacks to 1 column under sm. */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr' },
            gap: 2,
            alignItems: 'start',
          }}
        >
          <StarredSelect
            label={en.wizard.describe.steps.creationDate.creationDate.label}
            options={en.wizard.describe.steps.creationDate.creationDate.options}
            value={selectedDateType}
            onChange={(value) => {
              setSelectedDateType(value);
              setCreationDate(prev => ({ ...prev, type: value }));
              debouncedSave();
            }}
          />
          <DatePickerField
            dateType={selectedDateType}
            value={creationDate.value}
            onChange={(value) => {
              if (saveTimeoutRef.current) {
                clearTimeout(saveTimeoutRef.current);
              }
              setCreationDate(prev => ({ ...prev, value }));
            }}
            onAccept={() => saveDataRef.current?.()}
            onBlur={handleBlur}
          />
          <PrivacyFieldContainer fieldPath="describe.locationOfDate" togglePosition="inline">
            <AutocompleteFieldMultiSelectWithFavorites
              label={en.wizard.describe.steps.creationDate.locationOfDate.label}
              value={creationDate.location || []}
              options={dateLocations}
              onChange={(value) => {
                setCreationDate(prev => ({ ...prev, location: value }));
                debouncedSave();
              }}
              loading={signatureLoading}
              vocabularyType="date_location"
            />
          </PrivacyFieldContainer>
        </Box>

        {/* "As displayed on the object" reveal — Date Display behind a single button,
            matching the design's pattern. Auto-revealed when data already exists. */}
        <AsWrittenSubsection
          heading="As displayed on the object"
          defaultRevealed={!!creationDate.display?.trim()}
        >
          <Typography variant="subtitle2">
            {en.wizard.describe.steps.creationDate.dateDisplay.title}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
            {en.wizard.describe.steps.creationDate.dateDisplay.subtitle}
          </Typography>
          <PrivacyFieldContainer fieldPath="describe.dateDisplay" togglePosition="inline">
            <TextField
              fullWidth
              placeholder={en.wizard.describe.steps.creationDate.dateDisplay.label}
              variant="outlined"
              value={creationDate.display || ''}
              onChange={(e) => {
                setCreationDate(prev => ({ ...prev, display: e.target.value }));
                debouncedSave();
              }}
              onBlur={handleBlur}
            />
          </PrivacyFieldContainer>
        </AsWrittenSubsection>

      </WizardAccordion>

      <WizardAccordion
        title={en.wizard.additionalNotes.title}
        subtitle={en.wizard.additionalNotes.subtitle}
        defaultExpanded={false}
        summary={notesSummary}
        privacyFieldPath="describe.notes"
      >
        {/* Single textarea (decision #3). Legacy multi-entry data is auto-joined
            via `notesText` on read; saves write back as a single-element array
            to preserve the existing stepNotes.notes API shape. */}
        <TextField
          fullWidth
          multiline
          minRows={4}
          placeholder={en.wizard.describe.additionalNotes.placeholder}
          value={notesText}
          onChange={(e) => {
            const next = e.target.value;
            setStepNotes(prev => ({ ...prev, notes: next ? [next] : [] }));
            debouncedSave();
          }}
          onBlur={handleBlur}
        />
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          These notes are part of the public catalog entry. For private-only
          notes, see <strong>Always Private</strong> in the sidebar.
        </Typography>
      </WizardAccordion>
    </Box>
  );
};
